import type { AdminMarketDto, MarketStatus } from '@bravo/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/DataTable';
import { Badge, Button, Empty, ErrorBlock, PageHeader, Spinner, statusTone } from '../components/ui';
import { apiPage } from '../lib/api';
import { useFormat } from '../lib/format';

const FILTERS: Array<MarketStatus | 'all'> = ['all', 'draft', 'open', 'locked', 'resolved', 'voided'];

export function MarketsPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const [status, setStatus] = useState<MarketStatus | 'all'>('all');
  const query = useInfiniteQuery({
    queryKey: ['markets', status],
    queryFn: ({ pageParam }) =>
      apiPage<AdminMarketDto>('/admin/markets', {
        limit: 20,
        cursor: pageParam,
        ...(status === 'all' ? {} : { status }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const columns: ColumnDef<AdminMarketDto>[] = [
    { id: 'title', header: t('markets.titleCol'), cell: ({ row }) => format.text(row.original.title) },
    { id: 'category', header: t('markets.category'), cell: ({ row }) => format.category(row.original.category) },
    {
      id: 'status',
      header: t('status'),
      cell: ({ row }) => (
        <Badge tone={statusTone(row.original.status)}>{format.status(row.original.status)}</Badge>
      ),
    },
    { id: 'pool', header: t('markets.pool'), cell: ({ row }) => format.etb(row.original.totalPool) },
    { id: 'closes', header: t('markets.closes'), cell: ({ row }) => format.when(row.original.closesAt) },
  ];

  return (
    <div>
      <PageHeader
        title={t('markets.title')}
        subtitle={t('markets.subtitle')}
        actions={
          <Link to="/markets/new">
            <Button data-testid="market-new">{t('markets.new')}</Button>
          </Link>
        }
      />
      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((item) => (
          <Button key={item} variant={item === status ? 'primary' : 'secondary'} onClick={() => setStatus(item)}>
            {item === 'all' ? t('markets.all') : format.status(item)}
          </Button>
        ))}
      </div>
      {query.isLoading ? <Spinner /> : null}
      {query.isError ? <ErrorBlock message={t('errorTitle')} onRetry={() => void query.refetch()} /> : null}
      {!query.isLoading && items.length === 0 ? <Empty title={t('markets.empty')} /> : null}
      {items.length > 0 ? (
        <DataTable columns={columns} data={items} onRow={(row) => navigate(`/markets/${row.id}`)} />
      ) : null}
      {query.hasNextPage ? (
        <Button className="mt-4" variant="secondary" onClick={() => void query.fetchNextPage()}>
          {t('loadMore')}
        </Button>
      ) : null}
    </div>
  );
}
